import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, ElementRef, inject, OnDestroy, OnInit, Signal, viewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { MovilityType, SendRegisterUserResponse, TripType } from './interfaces/register-user.response';
import { provideNativeDateAdapter } from '@angular/material/core';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { CUSTOM_ROUTE_MOBILE_MAP } from '../custom-route/components/custom-route-mobile/config/constants';
import { RestService } from 'src/app/services/rest.service';
import { CUSTOM_ROUTE_DESKTOP_MAP } from '../custom-route/components/custom-route-desktop/config/constants';
import { initialTransportMeans } from './shared/components/transport-means-field/config/constants';
import { CustomRouteService } from '../custom-route/services/custom-route.service';
import { CustomRouteResponseCompleted, isCustomRouteResponseCompleted } from '../custom-route/interfaces/custom-route.response';
import { switchMap, filter, first } from 'rxjs';
import { Router } from '@angular/router';
import { CustomRoute } from '../custom-route/interfaces/custom-route.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { MatDialogService } from 'src/app/services/mat-dialog.service';
import { DialogComponent } from 'src/app/shared/dialogs/dialog/dialog.component';
import { HttpErrorResponse } from '@angular/common/http';
import { minArrayLengthValidator } from './shared/form/validators';
import { Loader1Component } from 'src/app/shared/loaders/loader-1/loader-1.component';
import { RouteDataService } from '../custom-route/shared/map/services/route-data.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoaderCarousel } from './shared/components';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { RegisterUserFormDesktopComponent, RegisterUserFormMobileComponent } from './components';
import { RegisterUserFormService, RegisterUserService } from './services';
import { GeolocationRequestService } from './components/register-user-form-mobile/services';
import { GeolocationRequestComponent } from "./components/register-user-form-mobile/components";
import { RegisterUserForm } from './interfaces';

@Component({
  selector: 'app-register-user',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    Loader1Component,
    MatTooltipModule,
    RegisterUserFormMobileComponent,
    RegisterUserFormDesktopComponent,
    GeolocationRequestComponent,
    LoaderCarousel
  ],
  providers: [DatePipe, provideNativeDateAdapter()],
  templateUrl: './register-user.component.html',
  styleUrls: ['./register-user.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterUserComponent implements OnInit, OnDestroy {
  public formRef: Signal<ElementRef | undefined> = viewChild('formRef');
  public loader: Signal<ElementRef | undefined> = viewChild('loader');
  
  private _fb = inject(FormBuilder);
  private _router = inject(Router);
  private _translateService = inject(TranslateService);
  private _sessionStorageService = inject(SessionStorageService);
  private _restService = inject(RestService);
  private _registerUserService = inject(RegisterUserService);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _geolocationRequestService = inject(GeolocationRequestService);
  private _customRouteService = inject(CustomRouteService);
  private _breakpointsService = inject(BreakpointsService);
  private _routeDataService = inject(RouteDataService);
  private _matDialogService = inject(MatDialogService);
  private _destroyRef = inject(DestroyRef);
  
  protected isLoadingRegisterUserData = this._registerUserService.isLoadingRegisterUserData;
  protected isLoadingFirstCustomRoute = this._registerUserService.isLoadingFirstCustomRoute;
  protected registerUser = this._registerUserService.registerUser;
  private _customRouteData = this._customRouteService.customRouteData;
  private _currentOrder = this._customRouteService.currentOrder;
  protected isMobile = this._breakpointsService.isMobile;
  protected isGeolocationView = this._geolocationRequestService.isGeolocationView;

  public form: FormGroup<RegisterUserForm> = this._fb.group<RegisterUserForm>(
    {
      startDate: new FormControl<Date | null>(null, Validators.required),
      endDate: new FormControl<Date | null>(null, Validators.required),
      startTime: new FormControl<string | null>(null),
      endTime: new FormControl<string | null>(null),
      startLocationAddress: new FormControl<string>(null, Validators.required),
      startLocationLat: new FormControl<number>(null, Validators.required),
      startLocationLng: new FormControl<number>(null, Validators.required),
      endLocation: new FormControl<boolean>(false, Validators.required),
      movilityType: new FormControl<MovilityType | null>(null),
      tripType: new FormControl<TripType>(null, Validators.required),
      interestIdList: new FormControl<number[]>(null,[Validators.required, minArrayLengthValidator(3)]),
      transportMeans: new FormControl<string[]>([initialTransportMeans.value], Validators.required),
      maxDistanceWalking: new FormControl<number>(200, Validators.required),
      geolocation: new FormControl<boolean>(false),
      subdomain: new FormControl<string>(null, Validators.required),
    }
  );
  
  constructor() {
    this._registerUserService.getDataRegisterUserServiceSignal();
    
    this._registerUserFormService.resetForm(this.form);
    
    this._customRouteService.reset();

    this._routeDataService.resetRouteSegments();
    this._routeDataService.resetCurrentStepAndTrame();
    
    // Set welcome planner view to true every time the user opens the register user page
    this._customRouteService.setIsWelcomeViewShown(true);
    
    // Reset mark all form fields as touched
    this._registerUserFormService.setMarkAllFormFieldsAsTouched(false);
  }
  
  ngOnInit(): void {
    // Every time the user opens the register user page:
    // set the custom route mobile views state to planner
    // and the custom route desktop views state to map.
    this._sessionStorageService.setItem('customRouteMobileViewsState', CUSTOM_ROUTE_MOBILE_MAP.planner);
    this._sessionStorageService.setItem('customRouteDesktopViewsState', CUSTOM_ROUTE_DESKTOP_MAP.map);
    
    // Set the cliendId to the form group
    const subdomain = this._restService.subDomain;
    this.form.get('subdomain').setValue(subdomain);
    
    // reset registerUserForm from local storage
    this._sessionStorageService.removeItem('registerUserForm');
  }

  public handleSubmit() {    
    if (this.form.invalid) {
      return;
    }

    this._sessionStorageService.setItem<boolean>('geolocation', this.form.value.geolocation);

    // 1) Set the number of days for the custom route in the service and session storage
    const diffDays = this.calculateNumberOfDaysCustomRoute();
    this._registerUserService.setNumberOfDaysCustomRoute(diffDays);

    // type form values to RegisterUserForm
    const formValues = this.form.value as unknown as RegisterUserForm;
    
    // set the register user form in the service (signal and session storage)
    this._registerUserService.setRegisterUserFormData(formValues);

    // 2) Llamada al endpoint de registro y a continuación polling
    this._registerUserService.sendRegisterUserData(formValues)
      .pipe(
        switchMap((res1: SendRegisterUserResponse) => {
          console.log('Register user response:', res1);
          // save token to the custom route service
          this._customRouteService.setToken(res1.token);
          
          // save token to session storage
          this._sessionStorageService.setItem<string>('customRouteToken', res1.token);

          // reset dayToShowIndex
          this._sessionStorageService.setItem<number>('dayToShowIndex', 0);  

          // 3) wait for the first custom route to be completed
          return this._customRouteService.launchRoutePlanning(res1.token, this._currentOrder()).pipe(
            filter(isCustomRouteResponseCompleted),
            first()
          );
        }),
        takeUntilDestroyed(this._destroyRef)
      ).subscribe({
        next: (completedResp) => {
          console.log('Custom route completed:', completedResp);

          if (completedResp.status === 200){
            this._customRouteService.getCustomRouteResponse(
              this._sessionStorageService.getItem('customRouteToken'), 
              this._currentOrder()
            ).subscribe({
              next: (res: CustomRouteResponseCompleted) => {
                this._registerUserService.setIsLoadingFirstCustomRoute(false);
                console.log('Custom route response:', res);
               
                if(res.dailyRoutes.points.length > 0) {
                  this._customRouteService.addDailyRoute(res);
                  this._sessionStorageService.setItem<CustomRoute>('customRouteData',  this._customRouteData());        
                  this._customRouteService.setCurrentOrder(this._currentOrder() + 1);
                  this._sessionStorageService.setItem<number>('customRouteCurrentOrder', this._customRouteService.currentOrder());
                  this._router.navigate(['/custom-route']);
                } else {
                  this._openNoPointsErrorDialog();
                }
              },
              error: (error: HttpErrorResponse) => {
                console.error('Error fetching custom route data:', error);
                this._registerUserService.setIsLoadingFirstCustomRoute(false);
                this._openServerErrorDialog(error.error.error);
                this._sessionStorageService.removeAll();
              }
            });
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error registering user:', error);
          this._registerUserService.setIsLoadingFirstCustomRoute(false);
          this._openServerErrorDialog('No se ha podido enviar el registro. Inténtalo de nuevo más tarde.');
          this._sessionStorageService.removeAll();
        }
      });
  }
  
  calculateNumberOfDaysCustomRoute(): number {
    dayjs.extend(customParseFormat); // to parse dates in 'DD/MM/YYYY' format

    const rawStartDate = this.form.get('startDate')?.value;
    const rawEndDate = this.form.get('endDate')?.value;

    if (rawStartDate && rawEndDate) {
      const startDate = dayjs(rawStartDate, 'DD/MM/YYYY');
      const endDate = dayjs(rawEndDate, 'DD/MM/YYYY');

      if (!startDate.isValid() || !endDate.isValid()) {
        console.error('Start or end date is invalid');
        return;
      }

      return endDate.diff(startDate, 'day') + 1; // +1 para incluir el día inicial
    }
  }
  
  private _openNoPointsErrorDialog(): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: DialogComponent,
      data: {
        iconName: 'alert',
        title: this._translateService.instant('Registro no válido'),
        content: this._translateService.instant('Con tu selección de filtros actuales no se puede configurar una ruta óptima para ti. Revise sus preferencias.'),
        primaryActionButtonText: this._translateService.instant('Aceptar'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    })
  }
  
  private _openServerErrorDialog(errorMessage: string): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: DialogComponent,
      data: {
        iconName: 'alert',
        title: this._translateService.instant('Error al enviar el registro'),
        content: this._translateService.instant(errorMessage),
        primaryActionButtonText: this._translateService.instant('Aceptar'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    })
  }
  
  ngOnDestroy(): void {
    this._registerUserService.setIsLoadingFirstCustomRoute(false);
  }
}
